import { initialActionState } from 'src/store/modules/runbooks/actions/ActionsHelper'
import assignIn from 'lodash/assignIn'

/**
 * Traverses all runbook actions and returns
 * them in the order in which they should be rendered
 * @returns {[]}
 */
export const createActionsList = function () {
    const ids = []
    this.applyRunbookFilters()
    if (this.topAction) this.addActionToList(this.topAction, ids)
    return ids
}

export const addActionToList = function (uid, ids, withChildren = true) {
    const action = this.actionsMap[uid]

    if (!action) {
        return
    }

    ids.push(action.uid)

    if (withChildren) {
        for (let childUid of action.children) {
            this.addActionToList(childUid, ids, true)
        }
    }

    if (action.nextSibling && !ids.includes(action.nextSibling)) {
        this.addActionToList(action.nextSibling, ids, withChildren)
    }
}

export const validateRunbook = function () {
    try {
        this.createActionsList()
    } catch (e) {
        return {
            success: false,
            message: this.CIRCULAR_TREE_MESSAGE,
            warnings: [],
        }
    }

    if (this.topAction) {
        const { success, message, warnings } = this.processAction(
            this.topAction,
            [],
            [],
            {}
        )
        return {
            success,
            message,
            warnings: warnings
                ? Object.values(warnings).filter(
                      (warning) => !(!warning || warning === 'NOK')
                  )
                : [],
        }
    }
    return {
        success: true,
        message: this.VALID_TREE_MESSAGE,
        warnings: [],
    }
}

export const findLastSibling = function (id) {
    const ids = Object.keys(this.actionsMap)
    if (ids.length === 0) {
        return null
    }
    const currentAction = this.actionsMap[id]
    if (currentAction && currentAction.nextSibling) {
        return this.findLastSibling(currentAction.nextSibling)
    }
    return id
}

export const indexAction = function ({ action, treeParent }) {
    // action executed before this one
    // this.actionsMap[action.uid].prevSibling = action.prevSibling
    // action which will execute after this one succeeds
    // this.actionsMap[action.uid].nextSibling = action.nextSibling,
    // all level 1 child actions (includes their nextSibling)
    if (this.actionsMap[action.uid]) {
        this.actionsMap[action.uid].treeChildren = []
        // all children which execute in parallel
        // this.actionsMap[action.uid].executionChildren = action.children
        this.actionsMap[action.uid].treeParent = treeParent || null
        // prereq actions, without overrides an action which
        // has execution parents cannot start/succced before its parents are green
        // action.parents
        this.actionsMap[action.uid].depth =
            treeParent && this.actionsMap[treeParent]
                ? this.actionsMap[treeParent].depth + 1
                : 0
    }

    if (treeParent && this.actionsMap[treeParent]) {
        this.actionsMap[treeParent].treeChildren.push(action.uid)
        if (
            this.actionsMap[action.uid] &&
            !this.actionsMap[action.uid].prevSibling &&
            !this.actionsMap[treeParent].children.includes(action.uid)
        ) {
            this.actionsMap[treeParent].children.push(action.uid)
        }
    }
}

export const createNewAction = function ({ data }) {
    const newAction = {
        ...initialActionState,
        // need to do this to not mutate initial object
        children: [],
        parents: [],
        treeChildren: [],
        ...data,
        uid: this.lastUid,
    }
    this.actionsMap[newAction.uid] = newAction
    this.lastUid += 1
    return newAction
}

export const addNewAction = function ({ data, options, device }) {
    // TODO: In case first action is not 1 or gets deleted, we be dead
    const lastSibling = this.findLastSibling(this.topAction)
    const newAction = this.createNewAction({ data: data })
    let fromAction
    if (options && options.fromAction) {
        fromAction = this.actionsMap[options.fromAction]
        if (fromAction && fromAction['infoOnly']) {
            newAction['infoOnly'] = true
        }
    } else {
        if (lastSibling !== null && this.actionsMap[lastSibling]) {
            // Check lastSibling and its existence in map
            fromAction = this.actionsMap[lastSibling]
        }
        options = { parent: false }
    }
    if (fromAction) {
        if (options.parent) {
            fromAction.children.push(newAction.uid)
            this.indexAction({
                action: newAction,
                treeParent: fromAction.uid,
            })
            newAction.visible = fromAction.expanded
        } else {
            if (
                fromAction.nextSibling &&
                this.actionsMap[fromAction.nextSibling]
            ) {
                this.actionsMap[fromAction.nextSibling].prevSibling =
                    newAction.uid
            }
            newAction.nextSibling = fromAction.nextSibling
            newAction.prevSibling = fromAction.uid
            fromAction.nextSibling = newAction.uid
            const fromActionInMap = this.actionsMap[fromAction.uid]
            this.indexAction({
                action: newAction,
                treeParent: fromActionInMap ? fromActionInMap.treeParent : null,
            })
            newAction.visible = fromAction.visible
        }
    } else {
        if (!this.topAction) {
            this.topAction = newAction.uid
        }
        this.indexAction({ action: newAction })
    }
    if (device) {
        if (this.ciToUid[newAction.device]) {
            this.ciToUid[newAction.device].uids[newAction.uid] = true
        } else {
            this.ciToUid[newAction.device] = {
                uids: {
                    [newAction.uid]: true,
                },
                details: {
                    ...device,
                },
            }
        }
    }

    if (newAction.depth === 0) {
        newAction.visible = true
    }
    return newAction
}

/**
 * Refreshes an action using a device.
 */
export const refreshActionFromDevice = function (uid, device) {
    const action = this.actionsMap[uid]
    if (!action) {
        return
    }

    const newData = this.mapDeviceToAction(device)
    if (action.freezeOwnerTeam) {
        delete newData['cmdbOwnerTeam']
        delete newData['cmdbOwnerTeamId']
        delete newData['ownerTeam']
        delete newData['ownerTeamId']
    }
    assignIn(action, newData)
    const deviceData = this.ciToUid[device.ci || device.id]
    if (!deviceData) {
        throw new Error(
            `Cannot refresh action ${uid} from device ${device.ci || device.id}, no mapping found in CMDB data.`
        )
    }
    if (deviceData) {
        this.ciToUid[device.ci || device.id].details = {
            ...device,
        }
    }

    // Mark the action as refreshed
    this.refreshCache[uid] = true
}

export const processedChildrenSuccessfully = function (
    children,
    processedChildren,
    warnings
) {
    // we check all processed children have existed successfully, without any warnings
    return (
        processedChildren.every((childProcess) => childProcess.success) &&
        children.every((child) => warnings[child] === null)
    )
}

export const handleInvalidChildrenMessage = function (
    actionUid,
    invalidChildren,
    warnings
) {
    const currentAction = this.actionsMap[actionUid]
    const name = currentAction
        ? `(${actionUid}) ${currentAction.name} => ${invalidChildren.map(
              (child) => child.name
          )}`
        : `(${actionUid}) Unknown Action => ${invalidChildren.map(
              (child) => child.name
          )}`
    return {
        success: false,
        message: `Cannot process child actions of (${actionUid}) '${
            currentAction ? currentAction.name : 'Unknown Action'
        }' due to unreachable parent dependencies: ${invalidChildren.map(
            (child) => child.name
        )}.`,
        name: name,
        warnings,
    }
}

export const checkChildrenLoops = function (
    action,
    processed,
    treeParents,
    warnings
) {
    // try to process unprocessed children again if they have dependencies on each other
    // we do that the number of children,
    // so if they are stupidly set to have dependencies on each other, we don't exit prematurely
    // ex step 1 has a dependency on step 2 which has a dependency on step 3 ...
    const { uid, children } = action
    for (let i = 0; i < children.length; i++) {
        const processedChildren = children.map((child) => {
            const processedAlready = processed.includes(child.toString())
            if (
                (processedAlready && warnings[child] !== null) ||
                !processedAlready
            ) {
                return this.processAction(
                    child,
                    processed,
                    treeParents,
                    warnings
                )
            }
            return {
                success: true,
                message: this.VALID_TREE_MESSAGE,
                warnings,
            }
        })
        if (
            this.processedChildrenSuccessfully(
                children,
                processedChildren,
                warnings
            )
        ) {
            warnings[uid] = null
            return {
                success: true,
                message: this.VALID_TREE_MESSAGE,
                warnings,
            }
        }
        if (i !== processedChildren.length - 1) continue
        // for the last iteration, decide if there are actually any loops can't be solved
        const invalidChildren = processedChildren.filter(
            (child) => !child.success
        )
        if (invalidChildren.length) {
            return this.handleInvalidChildrenMessage(
                action.uid,
                invalidChildren,
                warnings
            )
        } else {
            // if all children were processed correctly, make sure we remove the 'NOK' warning
            // otherwise, add an intermediary NOK warning to make sure we reprocess the tree
            //  to remove fixable warnings
            if (children.every((child) => warnings[child] === null)) {
                warnings[uid] = warnings[uid] === 'NOK' ? null : warnings[uid]
            } else {
                warnings[uid] = warnings[uid] ?? 'NOK'
            }
            return {
                success: true,
                message: this.VALID_TREE_MESSAGE,
                warnings,
            }
        }
    }
}

export const processChildren = function (
    action,
    processed,
    treeParents,
    warnings
) {
    const { children } = action
    if (children.length === 0)
        return {
            success: true,
            message: this.VALID_TREE_MESSAGE,
            warnings,
        }
    const processedChildren = children.map((child) =>
        this.processAction(child, processed, treeParents, warnings)
    )
    if (
        this.processedChildrenSuccessfully(
            children,
            processedChildren,
            warnings
        )
    ) {
        return {
            success: true,
            message: this.VALID_TREE_MESSAGE,
            warnings,
        }
    } else {
        return this.checkChildrenLoops(action, processed, treeParents, warnings)
    }
}

export const canProcessAction = function (
    actionUid,
    processed,
    treeParents,
    warnings
) {
    const action = this.actionsMap[actionUid]
    if (!action) {
        return false
    }

    return action.parents.every((parent) => {
        const parentWasProcessed = processed.includes(parent.toString())
        if (parentWasProcessed) {
            // make sure we don't overwrite a warning that's unrelated to another parent
            const prevWarning = warnings[actionUid]
            warnings[actionUid] =
                prevWarning?.includes(
                    `Parent dependency ${parent} of ${actionUid}`
                ) || prevWarning === 'NOK'
                    ? null
                    : prevWarning
            return true
        } else {
            const futureParentDependency = !treeParents.includes(parent)
            if (futureParentDependency) {
                warnings[
                    actionUid
                ] = `Parent dependency ${parent} of ${actionUid} causes logical error in action order, please fix.`
            }
            return futureParentDependency
        }
    })
}

export const processAction = function (
    actionUid,
    processed,
    treeParents,
    warnings
) {
    if (
        processed.includes(actionUid.toString()) &&
        warnings[actionUid] === null
    ) {
        return { success: true, message: this.VALID_TREE_MESSAGE }
    }
    const action = this.actionsMap[actionUid]
    if (!action) {
        return {
            success: false,
            message: `Action with UID ${actionUid} not found.`,
            warnings,
        }
    }
    warnings[actionUid] = null
    const canProcess = this.canProcessAction(
        actionUid,
        processed,
        treeParents,
        warnings
    )
    if (!canProcess) {
        const name = `(${actionUid}) '${action.name}'`
        return {
            success: false,
            message: `Parents of ${name} are unreachable.`,
            name,
            warnings,
        }
    }
    if (action.reverse) {
        const results = this.processChildren(
            action,
            processed,
            [...treeParents, actionUid],
            warnings
        )
        if (!results.success)
            return {
                success: false,
                message: results.message,
                name: results.name,
                warnings: results.warnings,
            }
        if (!processed.includes(actionUid.toString()))
            processed.push(actionUid.toString())
    } else {
        if (!processed.includes(actionUid.toString()))
            processed.push(actionUid.toString())
        const { success, message, name } = this.processChildren(
            action,
            processed,
            [...treeParents, actionUid],
            warnings
        )
        if (!success) return { success: false, message, name, warnings }
    }
    if (action.nextSibling)
        return this.processAction(
            action.nextSibling,
            processed,
            treeParents,
            warnings
        )
    return {
        success: true,
        message: this.VALID_TREE_MESSAGE,
        warnings,
    }
}

export const markActionForDeletion = function (uid) {
    const action = this.actionsMap[uid]
    if (action) {
        action.markedForDeletion = true
        action.treeChildren.map((c) => this.markActionForDeletion(c))
    }
}

export const deleteMarkedActions = function () {
    const removedUids = []
    Object.keys(this.actionsMap).forEach((uid) => {
        const action = this.actionsMap[uid]
        if (action && action.markedForDeletion) {
            removedUids.push(...this.deleteAction(uid))
        }
    })
    return removedUids
}

export const calculateMaxDepth = function () {
    let maxDepth = 0
    for (let actKey in this.actionsMap) {
        const act = this.actionsMap[actKey]
        if (act && act.depth > maxDepth) {
            maxDepth = act.depth
        }
    }
    return maxDepth
}

export const calculateTopActionUid = function () {
    // to be implemented
    return this.topAction
}

export const markActionInfoOnly = function (uid, newValue = null) {
    const action = this.actionsMap[uid]
    if (!action) {
        return
    }

    if (typeof newValue !== 'boolean') {
        newValue = !action.infoOnly
    }
    action.infoOnly = newValue
    // mark children
    action.treeChildren.forEach((a) => {
        this.markActionInfoOnly(a, newValue)
    })
}

export const mapDeviceToAction = function (device) {
    return {
        name: device.solution
            ? device.name
            : `${device.name ? device.name + ' ' : ''}${
                  device.ci || device.id
              }`,
        customerId: device.customer_id,
        customer: device.organization || device.customer,
        environment: device.cgk_environment || '',
        ownerTeamId: device.resolver_group_id || null,
        ownerTeam: device.resolver_group || '',
        cmdbOwnerTeamId: device.resolver_group_id || null,
        cmdbOwnerTeam: device.resolver_group || '',
        device: device.ci || device.id || '',
        deviceClass: device.cmdb_class,
        monitored: device.monitored_zenoss,
        children: [],
    }
}

// VT: this is disabled as Business indicated no sorting is required
// export const sortChildActions = function (
//     startUid,
//     actionsList,
//     parent = true
// ) {
//     actionsList.forEach((i) => {
//         const list = []
//         this.addActionToList(i, list, false)
//         list.sort().forEach((v, i, arr) => {
//             if (i === 0) {
//                 this.moveAction({ uid: v, toAction: startUid, parent })
//             } else {
//                 this.moveAction({
//                     uid: v,
//                     toAction: arr[i - 1],
//                     parent: false,
//                 })
//             }
//         })
//     })
// }

export const addSolutionWithChildren = function ({
    toAction,
    main,
    avZones,
    parent,
}) {
    // TODO: refactor this so it uses cmdb map
    //  this way we can add solutions recursively
    const newUids = []
    const newAction = this.addNewAction({
        data: this.mapDeviceToAction(main),
        options: {
            fromAction: toAction,
            parent,
        },
        device: main,
    })
    newUids.push(newAction.uid)
    if (main.child_solutions.length) {
        let before = newAction
        main.child_solutions.forEach((cs, i) => {
            before = this.addNewAction({
                data: this.mapDeviceToAction(cs),
                options: {
                    fromAction: before.uid,
                    parent: i === 0,
                },
                device: cs,
            })
            if (!newUids.includes(before.uid)) {
                newUids.push(before.uid)
            }
        })
    } else if (main.child_devices.length) {
        avZones.forEach((avZ) => {
            let avAction = this.addNewAction({
                data: {
                    name: avZ,
                    customerId: newAction.customerId,
                    customer: newAction.customer,
                    isAvZone: true,
                },
                options: { fromAction: newAction.uid, parent: true },
            })
            newUids.push(avAction.uid)
            main.child_devices
                .filter((d) => d.availability_zones.includes(avZ))
                .forEach((cs) => {
                    const d = this.addNewAction({
                        data: this.mapDeviceToAction(cs),
                        options: {
                            fromAction: avAction.uid,
                            parent: true,
                        },
                        device: cs,
                    })
                    newUids.push(d.uid)
                })
        })
    }
    return newUids
}

export const addAvAction = function (name, parent) {
    return this.addNewAction({
        data: {
            name: name,
            customerId: parent.customerId,
            customer: parent.customer,
            ownerTeam: parent.ownerTeam,
            ownerTeamId: parent.ownerTeamId,
            isAvZone: true,
        },
        options: {
            fromAction: parent.uid,
            parent: true,
        },
    })
}

export const markEmptySolutionsAsInfoOnly = function () {
    const { actionsMap, topAction } = this
    for (let itemKey in actionsMap) {
        const item = actionsMap[itemKey]
        if (
            item &&
            this.isSolution(item) &&
            !item.children.length &&
            item.uid !== topAction
        ) {
            item.infoOnly = true
        }
    }
}
